import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import vegetablesService from '../../services/vegetablesService';
import AddVegetableModal from '../sales/AddVegetableModal';
import EditVegetableModal from './EditVegetableModal';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DataTable from '../common/DataTable';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export default function VegetablesTab() {
  const [vegetables, setVegetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVegetable, setSelectedVegetable] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    fetchVegetables();
  }, []);

  const fetchVegetables = async () => {
    setLoading(true);
    try {
      const data = await vegetablesService.getVegetables();
      setVegetables(data);
    } catch (error) {
      console.error('Error fetching vegetables:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVegetableAdded = (newVegetable) => {
    setVegetables(prev => [...prev, newVegetable]);
    setShowAddModal(false);
  };

  const handleVegetableUpdated = (updatedVegetable) => {
    setVegetables(prev => 
      prev.map(veg => veg.id === updatedVegetable.id ? updatedVegetable : veg)
    );
    setShowEditModal(false);
  };

  const handleDeleteClick = (vegetable) => {
    setSelectedVegetable(vegetable);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await vegetablesService.deleteVegetable(selectedVegetable.id);
      setVegetables(prev => prev.filter(v => v.id !== selectedVegetable.id));
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting vegetable:', error);
    }
  };

  const columns = [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex justify-end gap-2">
          <button
            onClick={() => {
              setSelectedVegetable(row.original);
              setShowEditModal(true);
            }}
            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <PencilSquareIcon className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteClick(row.original)}
            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
            rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 
            focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Vegetable
        </button>
      </div>

      <DataTable
        data={vegetables}
        columns={columns}
        sorting={sorting}
        setSorting={setSorting}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        searchPlaceholder="Search vegetables..."
      />

      <AddVegetableModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onVegetableAdded={handleVegetableAdded}
      />

      {showEditModal && (
        <EditVegetableModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          vegetable={selectedVegetable}
          onVegetableUpdated={handleVegetableUpdated}
        />
      )}

      <ConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Vegetable"
        message={`Are you sure you want to delete ${selectedVegetable?.name}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}